import React, { useMemo } from "react";
import ThinScroll from "../../shared/ThinScroll";
import { fmtCurrency } from "../../shared/format.js";

/**
 * NormalsTable — beyond_forecast evidence component.
 *
 * Hero pair: arrivals_in_window + revenue_in_window, captioned
 * "Booked beyond where any forecast reaches."
 * Then a table of riskiest_dates styled to match ForecastTable
 * (Today Ahead: Revenue landing on rough days).
 * Label every number here as typical, never as forecast.
 *
 * Spec references: §7 (beyond_forecast), §2d (formatting).
 */

/** Format number with locale separators. */
const fmtNum = (v) => Number(v ?? 0).toLocaleString();

/** Short date — "Aug 10" */
const shortDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T00:00:00");
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

/** Day of week — "Sun" */
const dayOfWeek = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T00:00:00");
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { weekday: "short" });
};

/** Rain-day risk colour — higher is worse. */
const rainColor = (pct) => {
  if (pct >= 40) return "text-blue-500 dark:text-blue-400";
  if (pct >= 25) return "text-yellow-500 dark:text-yellow-400";
  return "text-gray-700 dark:text-gray-300";
};

const NormalsTable = ({ panel }) => {
  const f = panel.facts || {};
  const riskiest = f.riskiest_dates || [];

  // Sort riskiest dates chronologically (defensive — spec sample is pre-sorted).
  const sortedRiskiest = useMemo(
    () => [...riskiest].sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0)),
    [riskiest],
  );

  if (sortedRiskiest.length === 0 && f.arrivals_in_window == null) return null;

  return (
    <div className="space-y-4">
      {/* ─── Hero pair ──────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 gap-6">
        <div className="text-center">
          <p className="text-xs text-gray-400 dark:text-gray-500 mb-1">
            Tracked arrivals
          </p>
          <p className="text-3xl font-extrabold text-gray-900 dark:text-gray-100 leading-none">
            {fmtNum(f.arrivals_in_window)}
          </p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
            {f.days_covered} days · {fmtCurrency(f.revenue_in_window, { dropCents: true })}
          </p>
        </div>
        <div className="text-center">
          <p className="text-xs text-gray-400 dark:text-gray-500 mb-1">
            Typical high
          </p>
          <p className="text-3xl font-extrabold text-orange-500 leading-none">
            {f.typical_high_f}°
          </p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
            Rained in {f.typical_rain_day_pct}% of years
          </p>
        </div>
      </div>

      {/* ─── Table ──────────────────────────────────────────────────────────── */}
      {sortedRiskiest.length > 0 && (
        <ThinScroll className="overflow-x-auto responsive-card-table-wrapper">
          <table className="w-full text-sm responsive-card-table">
            <thead className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
              <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
                <th className="px-3 py-2.5 text-left font-semibold">Date</th>
                <th className="px-3 py-2.5 text-right font-semibold">Typical high</th>
                <th className="px-3 py-2.5 text-right font-semibold">Rained in</th>
                <th className="px-3 py-2.5 text-right font-semibold">Arrivals</th>
              </tr>
            </thead>
            <tbody>
              {sortedRiskiest.map((d, i) => {
                const highRisk = d.rain_day_pct >= 40;

                return (
                  <tr
                    key={d.date ?? i}
                    className={`border-b border-gray-100 dark:border-gray-800 transition ${
                      highRisk
                        ? "bg-orange-50/60 dark:bg-orange-900/10"
                        : "hover:bg-gray-50 dark:hover:bg-gray-800/50"
                    }`}
                  >
                    {/* Date */}
                    <td data-label="Date" className="px-3 py-2.5 whitespace-nowrap">
                      <span className="text-gray-400 dark:text-gray-500 mr-1">
                        {dayOfWeek(d.date)}
                      </span>
                      <span className="font-medium text-gray-900 dark:text-gray-100">
                        {shortDate(d.date)}
                      </span>
                    </td>

                    {/* Typical high */}
                    <td data-label="Typical high" className="px-3 py-2.5 text-right whitespace-nowrap">
                      <span className="font-semibold text-orange-500 dark:text-orange-400">
                        {d.typical_high_f != null ? `${d.typical_high_f}°` : "—"}
                      </span>
                    </td>

                    {/* Rained in */}
                    <td data-label="Rained in" className="px-3 py-2.5 text-right whitespace-nowrap">
                      <span className={`font-semibold ${rainColor(d.rain_day_pct)}`}>
                        {d.rain_day_pct != null ? `${d.rain_day_pct}% of years` : "—"}
                      </span>
                    </td>

                    {/* Arrivals */}
                    <td data-label="Arrivals" className="px-3 py-2.5 text-right font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap">
                      {fmtNum(d.arrivals)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </ThinScroll>
      )}
    </div>
  );
};

export default NormalsTable;
